// F-MOB-M6: persistent banner shown when iOS DeviceOrientationEvent
// permission is denied (or never resolves). Tells the user how to re-enable
// motion access in Safari and offers a "Try again" button that re-runs the
// permission request via the supplied callback.

let bannerEl: HTMLDivElement | null = null;

export interface GyroPermissionBannerOptions {
  /** Called when the user taps "Try again". Should re-run the permission flow. */
  onRetry: () => Promise<boolean> | boolean;
  /** Called when the user dismisses the banner. */
  onDismiss?: () => void;
}

/** Show the permission-denied banner. Idempotent — repeated calls re-mount. */
export function showGyroPermissionBanner(opts: GyroPermissionBannerOptions): void {
  if (typeof document === 'undefined') return;
  hideGyroPermissionBanner();

  bannerEl = document.createElement('div');
  bannerEl.id = 'gyro-permission-banner';
  Object.assign(bannerEl.style, {
    position: 'fixed',
    top: 'calc(8px + env(safe-area-inset-top, 0px))',
    left: '50%',
    transform: 'translateX(-50%)',
    maxWidth: 'min(90vw, 540px)',
    padding: '10px 14px',
    background: 'rgba(40, 24, 24, 0.94)',
    border: '1px solid #6a3a3a',
    borderRadius: '4px',
    color: '#e6c8c8',
    fontFamily: 'Courier New, monospace',
    fontSize: '12px',
    lineHeight: '1.4',
    zIndex: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  } satisfies Partial<CSSStyleDeclaration>);

  const message = document.createElement('div');
  message.style.flex = '1';
  message.innerHTML =
    '<b style="color:#ff8888">Gyro denied.</b> Open Safari Settings → Motion &amp; Orientation Access to re-enable.';
  bannerEl.appendChild(message);

  const retry = document.createElement('button');
  retry.type = 'button';
  retry.textContent = 'Try again';
  Object.assign(retry.style, {
    padding: '6px 10px',
    background: 'rgba(80, 90, 60, 0.5)',
    border: '1px solid #4a5a3a',
    borderRadius: '3px',
    color: '#c8c090',
    fontSize: '12px',
    cursor: 'pointer',
  } satisfies Partial<CSSStyleDeclaration>);
  retry.addEventListener('click', async () => {
    try {
      const ok = await opts.onRetry();
      if (ok) hideGyroPermissionBanner();
    } catch {
      // Leave banner visible — user can tap retry again.
    }
  });
  bannerEl.appendChild(retry);

  const dismiss = document.createElement('button');
  dismiss.type = 'button';
  dismiss.textContent = 'X';
  dismiss.setAttribute('aria-label', 'Dismiss');
  Object.assign(dismiss.style, {
    padding: '4px 8px',
    background: 'transparent',
    border: 'none',
    color: '#888',
    fontSize: '14px',
    cursor: 'pointer',
  } satisfies Partial<CSSStyleDeclaration>);
  dismiss.addEventListener('click', () => {
    hideGyroPermissionBanner();
    opts.onDismiss?.();
  });
  bannerEl.appendChild(dismiss);

  document.body?.appendChild(bannerEl);
}

/** Hide the banner if visible. Safe to call when not shown. */
export function hideGyroPermissionBanner(): void {
  if (!bannerEl) return;
  bannerEl.remove();
  bannerEl = null;
}

/** Test/inspection helper. */
export function isGyroPermissionBannerVisible(): boolean {
  return bannerEl !== null;
}
